/**
 * Evaluation utility functions shared across PO field components.
 *
 * These handle reading scores from the LLM-as-judge evaluation structure
 * (evaluation.field_scores) and provide backward compatibility with the
 * legacy flat confidence object format.
 *
 * Evaluation structure (new):
 *   evaluation: {
 *     overall_score: 0.92,
 *     field_scores: {
 *       "header.poNumber": { score: 0.95, issue: null },
 *       "parties[0].name": { score: 0.9, issue: null },
 *       ...
 *     },
 *     flags: [...],
 *     summary: "..."
 *   }
 *
 * Legacy confidence structure (old, for backward compat):
 *   confidence: {
 *     "header.poNumber": 0.95,
 *     overall: 0.85,
 *     ...
 *   }
 */

/**
 * Check if a field value is considered "empty" (absent).
 * @param {*} val - The field value
 * @returns {boolean}
 */
export function isFieldEmpty(val) {
  return val === null || val === undefined || val === '';
}

/**
 * Resolve a dot-notation key against a data object.
 * e.g., resolveFieldValue(partyData, 'address.city') -> partyData.address.city
 * @param {Object} obj - The data object
 * @param {string} key - Dot-notation key
 * @returns {*} The resolved value
 */
export function resolveFieldValue(obj, key) {
  if (!key.includes('.')) return obj?.[key];
  return key.split('.').reduce((o, k) => o?.[k], obj);
}

/**
 * Unwrap evaluation if it was double-nested ({"evaluation": {...}}).
 * Early pipeline versions stored the wrapper; newer ones store the inner dict.
 * @param {Object|null} evaluation
 * @returns {Object|null}
 */
export function unwrapEvaluation(evaluation) {
  if (evaluation && !evaluation.field_scores && evaluation.evaluation) {
    return evaluation.evaluation;
  }
  return evaluation;
}

/**
 * Get the score for a field from evaluation or legacy confidence data.
 *
 * Checks the new evaluation.field_scores structure first, then falls
 * back to the legacy flat confidence object for old queue entries.
 *
 * @param {Object|null} evaluation - The evaluation object (new format)
 * @param {Object|null} confidence - The confidence object (legacy format)
 * @param {string} fieldPath - Dot-notation field path (e.g., "header.poNumber")
 * @returns {number|null} Score 0.0-1.0, or null if not available
 */
export function getFieldScore(evaluation, confidence, fieldPath) {
  // Try new evaluation format first (unwrap if double-nested)
  const eval_ = unwrapEvaluation(evaluation);
  if (eval_?.field_scores) {
    const fieldData = eval_.field_scores[fieldPath];
    if (fieldData != null) {
      // field_scores entries can be { score, issue } objects or raw numbers
      return typeof fieldData === 'object' ? fieldData.score : fieldData;
    }
  }

  // Fall back to legacy confidence object
  if (confidence != null && confidence[fieldPath] != null) {
    return confidence[fieldPath];
  }

  return null;
}

/**
 * Get the overall score from evaluation or legacy confidence.
 *
 * @param {Object|null} evaluation - The evaluation object (new format)
 * @param {Object|null} confidence - The confidence object (legacy format)
 * @returns {number|null} Overall score 0.0-1.0, or null if not available
 */
export function getOverallScore(evaluation, confidence) {
  const eval_ = unwrapEvaluation(evaluation);
  if (eval_?.overall_score != null) {
    return eval_.overall_score;
  }
  if (confidence?.overall != null) {
    return confidence.overall;
  }
  return null;
}

/**
 * Get evaluation flags (hallucinations, mismatches, missing fields).
 *
 * @param {Object|null} evaluation - The evaluation object
 * @returns {Array} Array of flag objects, empty if no evaluation
 */
export function getEvaluationFlags(evaluation) {
  const eval_ = unwrapEvaluation(evaluation);
  return eval_?.flags || [];
}

/**
 * Get unified issues from a queue entry.
 *
 * New entries have a top-level `issues` array populated by the pipeline.
 * Old entries only have `evaluation.flags`. This function reads from
 * `queueEntry.issues` when present and non-empty, else falls back to
 * converting `evaluation.flags` into the unified shape.
 *
 * @param {Object} queueEntry - The queue entry object
 * @param {Object|null} evaluation - The evaluation object (may also be on queueEntry)
 * @returns {Array} Array of unified issue objects, never null
 */
export function getUnifiedIssues(queueEntry, evaluation) {
  // Prefer the unified issues array if present and non-empty
  if (Array.isArray(queueEntry?.issues) && queueEntry.issues.length > 0) {
    return queueEntry.issues;
  }

  // Fallback: convert evaluation.flags for old entries
  const eval_ = unwrapEvaluation(evaluation || queueEntry?.evaluation);
  const flags = eval_?.flags;
  if (!Array.isArray(flags) || flags.length === 0) return [];

  const severityMap = {
    hallucination: 'error',
    mismatch: 'warning',
  };

  return flags.map(flag => ({
    source: 'evaluation',
    type: flag.type || 'unknown',
    severity: severityMap[flag.type] || 'info',
    field: flag.field || null,
    detail: flag.detail || '',
    stepId: null,
  }));
}

/**
 * Return the worst severity from an issues array.
 *
 * Severity hierarchy: error > warning > info.
 * Returns null if the array is empty or absent.
 *
 * @param {Array} issues - Array of issue objects with a `severity` field
 * @returns {'error'|'warning'|'info'|null}
 */
export function getWorstSeverity(issues) {
  if (!Array.isArray(issues) || issues.length === 0) return null;

  let worst = null;
  for (const issue of issues) {
    if (issue.severity === 'error') return 'error';
    if (issue.severity === 'warning') worst = 'warning';
    if (issue.severity === 'info' && worst === null) worst = 'info';
  }
  return worst;
}

/**
 * Filter issues to only those matching a specific field path.
 *
 * @param {Array} issues - Array of unified issue objects (from getUnifiedIssues)
 * @param {string} fieldPath - Dot-notation field path (e.g., "header.poNumber", "parties[0].name")
 * @returns {Array} Matching issues, empty array if none
 */
export function getFieldIssues(issues, fieldPath) {
  if (!Array.isArray(issues) || !fieldPath) return [];
  return issues.filter(issue => issue.field === fieldPath);
}

/**
 * Normalize confidence for display and rollup.
 *
 * Handles backward compatibility: the legacy extraction prompt encoded
 * "field not found" as confidence 0.0. The LLM-as-judge evaluator
 * uses proper scoring. This function normalizes legacy 0.0-on-empty
 * to 0.95 so aggregate MINs aren't dragged down.
 *
 * @param {number} conf - Raw confidence/evaluation score
 * @param {boolean} isEmpty - Whether the field value is empty
 * @returns {number} Normalized score
 */
export function normalizeConfidence(conf, isEmpty) {
  if (isEmpty && conf < 0.1) return 0.95;
  return conf;
}
